import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductShowcase() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (active) setProducts(res.data);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="shop"
      data-testid="shop-section"
      className="relative bg-[#FDFBF7] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D96C2B] mb-5">
              The Trio
            </div>
            <h2 className="font-display text-[#2C4C3B] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-2xl">
              Three bottles. <span className="italic">One obsession.</span>
            </h2>
          </div>
          <p className="text-[#4A5D53] text-base max-w-sm lg:text-right">
            Pick a favorite. Or take all three home — they were made to be
            tasted side by side.
          </p>
        </div>

        {loading && (
          <div
            data-testid="products-loading"
            className="text-[#4A5D53] text-sm py-20 text-center"
          >
            Brewing the menu…
          </div>
        )}
        {error && (
          <div
            data-testid="products-error"
            className="text-red-700 text-sm py-20 text-center"
          >
            Couldn&apos;t load products: {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-20">
            {products.map((p, idx) => (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className={`relative group flex flex-col ${
                  idx === 1 ? "md:translate-y-10" : ""
                }`}
              >
                <div className="relative h-[420px] bg-[#F4F1EA] flex items-end justify-center overflow-hidden">
                  <span
                    aria-hidden
                    className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.3em] text-[#4A5D53]"
                  >
                    0{idx + 1}
                  </span>
                  <span
                    aria-hidden
                    className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.3em] text-[#4A5D53]"
                  >
                    {p.size}
                  </span>
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="h-[440px] w-auto object-contain drop-shadow-[0_20px_25px_rgba(31,41,36,0.25)] translate-y-6 transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-[1.02]"
                  />
                </div>

                <div className="mt-6 flex items-start justify-between gap-6">
                  <div>
                    <h3 className="font-display text-2xl text-[#2C4C3B] tracking-tight leading-tight">
                      {p.name}
                    </h3>
                    <p className="mt-2 text-sm text-[#4A5D53] max-w-xs leading-relaxed">
                      {p.description}
                    </p>
                  </div>
                  <div className="font-display text-2xl text-[#1F2924] whitespace-nowrap">
                    ${p.price.toFixed(2)}
                  </div>
                </div>

                <button
                  data-testid={`add-to-cart-${p.id}`}
                  onClick={() => addItem(p, 1)}
                  className="mt-6 group/btn inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-[#D96C2B] text-[#FDFBF7] uppercase tracking-wider text-sm hover:bg-[#B75718] transition-colors w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  Add to Cart
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
